import { ArenaOpsUsdtSchema } from "./arena-ops-filters.dto";
import { z } from "zod";

/**
 * P3 — rake grouped by the seat composition of the duel that produced it,
 * because a human-versus-bot pot is only half real money.
 *
 * Grouped server-side, to the same six compositions plus the unattributed
 * bucket that `oracle.sql` prints. The composition rule stays in one place: a
 * panel regrouping campaign rows in the browser is a second copy of it, free to
 * drift. Campaign-level detail, if it is ever wanted, is a drill-down
 * endpoint rather than a payload the panel throws away.
 *
 * `composition: null` is the unattributed bucket — rake whose `idempotencyKey`
 * does not resolve to a `battle-arena-<id>` proposal: private-arena rooms,
 * `CREATOR_RAKE` included. It is returned as a row, never filtered, so the rows
 * foot to the ledger total.
 */
export const ArenaOpsRakeProvenanceRowSchema = z.object({
  /** `1H/0S/1B` — humans / staff / bots at the table. `null` on the unattributed bucket, which has no seats to describe. */
  composition: z.string().nullable(),
  seats: z.number().int(),
  humans: z.number().int(),
  staff: z.number().int(),
  bots: z.number().int(),
  /** Duels with this composition. */
  matches: z.number().int(),
  movementCount: z.number().int(),
  rakeUsdt: ArenaOpsUsdtSchema,
  /**
   * The `humans ÷ seats` share of each pot. Rake taken off a bot-versus-bot
   * duel is the house raking its own float, so reporting rake without this
   * split overstates revenue.
   */
  realUserRakeUsdt: ArenaOpsUsdtSchema,
});

export const ArenaOpsMoneyRakeProvenanceResponseSchema = z.object({
  rows: z.array(ArenaOpsRakeProvenanceRowSchema),
  totalUsdt: ArenaOpsUsdtSchema,
  realUserTotalUsdt: ArenaOpsUsdtSchema,
  unattributedUsdt: ArenaOpsUsdtSchema,
  unattributedMovementCount: z.number().int(),
});
export type ArenaOpsMoneyRakeProvenanceResponse = z.infer<typeof ArenaOpsMoneyRakeProvenanceResponseSchema>;
